import {
  BasicInfo,
  basicInfoFormSchema,
  BasicInfoStep,
} from "@/components/manage-personas/steps/BasicInfoStep";
import {
  appearanceFormSchema,
  AppearanceStep,
  PersonaAppearance,
} from "@/components/manage-personas/steps/AppearanceStep";
import {
  accountConfigFormSchema,
  AccountConfigStep,
  PersonaAccountConfig,
} from "@/components/manage-personas/steps/AccountConfigStep";
import {
  AdditionalBackground,
  additionalBackgroundFormSchema,
  AdditionalBackgroundStep,
} from "@/components/manage-personas/steps/AdditionalBackgroundStep";
import {
  KnowledgeBases,
  knowledgeBasesFormSchema,
  KnowledgeBasesStep,
} from "@/components/manage-personas/steps/KnowledgeBasesStep";
import { LoadingButton } from "@/components/LoadingButton";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { cn } from "@/lib/utils";
import { Persona } from "@/api/personas";
import { PersonaDocuments } from "@/api/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabUrlState } from "@/hooks/use-tab-url-state";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { QuickCreatePersonaModal } from "@/components/createPersona/QuickCreatePersonaModal";

export const EDIT_PERSONA_TABS = [
  { id: "profile", name: "Profile" },
  { id: "appearance", name: "Appearance" },
  { id: "connect", name: "Connect" },
  { id: "personality", name: "Personality" },
  { id: "knowledge", name: "Knowledge & Tools" },
] as const;

export function EditPersonaDetailsForm({
  personaData,
  documents,
  onSave,
  onCancel,
  children,
  loading: parentLoading,
}: PropsWithChildren<{
  personaData: Persona;
  documents: PersonaDocuments;
  onSave: (persona: Persona, documents: any) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}>) {
  const [tab, setTab] = useTabUrlState(
    EDIT_PERSONA_TABS.map((tabs) => tabs.id),
    "profile",
  );
  const [loading, setLoading] = useState(false);
  const [formResetCount, setFormResetCount] = useState(0);
  const scrollPosition = useRef(0);
  const isLoading = loading || parentLoading;
  const formReadOnly = isLoading;

  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);

  const handleQuickCreateClick = () => {
    setIsQuickCreateOpen(true);
  };

  const handleQuickCreateClose = () => {
    setIsQuickCreateOpen(false);
  };

  const handleQuickCreateSuccess = (savedPersona: Persona) => {
    console.log("AI Quick Create successful and saved:", savedPersona);
    setFormResetCount((prev) => prev + 1);
    handleQuickCreateClose();
  };

  const basicInfoDefaultValues = {
    name: personaData?.name,
    persona_id: personaData?.persona_id,
    short_bio: personaData?.short_bio ?? "",
    persona: personaData?.persona ?? "",
    interests: personaData?.interests
      ? (personaData?.interests.filter((l) => l !== "") ?? [])
      : [],
  } satisfies BasicInfo;
  const appearanceDefaultValues = {
    hair_color: personaData?.appearance?.hair_color ?? "",
    eye_color: personaData?.appearance?.eye_color ?? "",
    skin_tone: personaData?.appearance?.skin_tone ?? "",
    body_type: personaData?.appearance?.body_type ?? "",
    fashion: personaData?.appearance?.fashion ?? "",
    height: personaData?.appearance?.height ?? "",
    weight: personaData?.appearance?.weight ?? "",
    ethnicity: personaData?.appearance?.ethnicity ?? "",
    age: Number.parseInt(String(personaData?.age)) || undefined,
    gender: personaData?.gender || undefined,
  } satisfies PersonaAppearance;
  const accountConfigDefaultValues = {
    telegram: personaData?.telegram ?? "",
    usernames_to_engage: personaData?.usernames_to_engage ?? [],
    twitter_lists: personaData?.twitter_lists ?? [],
    persona_id: personaData?.persona_id ?? "",
  } satisfies PersonaAccountConfig;

  const additionalBackgroundDefaultValues = {
    bio: personaData?.bio ?? "",
    style: personaData?.style ?? "",
    target_audience: Array.isArray(personaData?.target_audience)
      ? personaData.target_audience.filter((l) => l !== "")
      : typeof personaData?.target_audience === "string"
        ? [personaData.target_audience].filter((l) => l !== "")
        : [],
    persona_objective: personaData?.persona_objective ?? "",
    occupation: personaData?.occupation ?? "",
  } satisfies AdditionalBackground;
  const knowledgeBasesDefaultValues = {
    // @ts-expect-error types are wrong - REVERTED
    core_personality: documents.core_personality?.filter((l) => l !== "") || [],
    // @ts-expect-error types are wrong - REVERTED
    static_data: documents.static_data?.filter((l) => l !== "") || [],
    // @ts-expect-error types are wrong - REVERTED
    live_data: documents.live_data?.filter((l) => l !== "") || [],
    tools: documents.tools?.filter((l) => l !== "") || [],
  } satisfies KnowledgeBases;
  const basicInfoForm = useForm<BasicInfo>({
    resolver: zodResolver(basicInfoFormSchema),
    defaultValues: basicInfoDefaultValues,
  });
  const appearanceForm = useForm<PersonaAppearance>({
    resolver: zodResolver(appearanceFormSchema),
    defaultValues: appearanceDefaultValues,
  });
  const accountConfigForm = useForm<PersonaAccountConfig>({
    resolver: zodResolver(accountConfigFormSchema),
    defaultValues: accountConfigDefaultValues,
  });
  const additionalBackgroundForm = useForm<AdditionalBackground>({
    resolver: zodResolver(additionalBackgroundFormSchema),
    defaultValues: additionalBackgroundDefaultValues,
  });
  const knowledgeBasesForm = useForm<KnowledgeBases>({
    resolver: zodResolver(knowledgeBasesFormSchema),
    // @ts-expect-error types are wrong - REVERTED
    defaultValues: knowledgeBasesDefaultValues,
  });

  useEffect(() => {
    console.log(
      "Resetting forms due to count:",
      formResetCount,
      basicInfoDefaultValues,
    );
    basicInfoForm.reset(basicInfoDefaultValues);
    appearanceForm.reset(appearanceDefaultValues);
    accountConfigForm.reset(accountConfigDefaultValues);
    additionalBackgroundForm.reset(additionalBackgroundDefaultValues);
    // @ts-expect-error types are wrong - REVERTED
    knowledgeBasesForm.reset(knowledgeBasesDefaultValues);
    // After reset, the forms are no longer dirty
  }, [personaData, documents, formResetCount]);

  // Helper to check if ANY form is dirty
  const isAnyFormDirty =
    basicInfoForm.formState.isDirty ||
    appearanceForm.formState.isDirty ||
    accountConfigForm.formState.isDirty ||
    additionalBackgroundForm.formState.isDirty ||
    knowledgeBasesForm.formState.isDirty;

  // Ask user if they really want to leave. This only works for client-side navigation.
  const blocker = useBlocker(isAnyFormDirty);

  // This only works for server-side navigation (or closing tab).
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (isAnyFormDirty) {
          e.preventDefault(); // Prevent closing page/tab
          e.returnValue = ""; // Necessary for some browsers
        }
      },
      [isAnyFormDirty],
    ),
  );

  const handleSave = async () => {
    try {
      const formStates = await Promise.all([
        validateForm(basicInfoForm),
        validateForm(appearanceForm),
        validateForm(accountConfigForm),
        validateForm(additionalBackgroundForm),
        validateForm(knowledgeBasesForm),
      ] as const);

      // Use the dirty state returned directly from validateForm (which uses form.formState.isDirty)
      const isFormDirty = formStates.some((form) => form.isDirty);
      const hasErrors = formStates.some((form) => !form.isValid);

      if (!isFormDirty && !hasErrors) {
        console.log("No changes detected, skipping save.");
        return;
      }

      if (hasErrors) {
        console.log("Form validation failed.");
        // Trigger validation on all forms to show errors
        await basicInfoForm.trigger();
        await appearanceForm.trigger();
        await accountConfigForm.trigger();
        await additionalBackgroundForm.trigger();
        await knowledgeBasesForm.trigger();
        return;
      }

      const [
        basicInfoFormValidation,
        appearanceFormValidation,
        accountConfigFormValidation,
        additionalBackgroundFormValidation,
        knowledgeBasesFormValidation,
      ] = formStates;

      setLoading(true);

      const age = appearanceFormValidation.values.age;
      const gender = appearanceFormValidation.values.gender;

      const updatedPersona = {
        ...personaData,
        ...basicInfoFormValidation.values,
        ...accountConfigFormValidation.values,
        ...additionalBackgroundFormValidation.values,
        age,
        gender,
        appearance: {
          ...personaData.appearance,
          ...appearanceFormValidation.values,
        },
      } satisfies Persona;
      const updatedDocuments = {
        ...documents,
        ...knowledgeBasesFormValidation.values,
      };

      await onSave(updatedPersona, updatedDocuments);
      // Forms will be reset via formResetCount increment, making them not dirty
      setFormResetCount((prev) => prev + 1);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationDialogClose = (
    reason: "cancel" | "save" | "navigate",
  ) => {
    if (reason === "save") {
      handleSave().then(() => {
        if (blocker.state === "blocked") {
          const hasErrors = [
            basicInfoForm,
            appearanceForm,
            accountConfigForm,
            additionalBackgroundForm,
            knowledgeBasesForm,
          ].some((form) => Object.keys(form.formState.errors).length > 0);
          if (!hasErrors) {
            blocker.proceed();
          }
        }
      });
    } else if (reason === "navigate" && blocker.state === "blocked") {
      blocker.proceed();
    } else {
      if (blocker.state === "blocked") {
        blocker.reset();
      }
    }
  };

  const handleScroll = () => {
    scrollPosition.current = window.scrollY;
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, scrollPosition.current);
  }, [tab]);

  return (
    <>
      <div className="w-full border-none bg-darkGray rounded-2xl pb-6">
        <div className="flex flex-col gap-6 mx-10 my-6">
          <Tabs value={tab} onValueChange={setTab}>
            <ScrollArea className="w-full">
              <TabsList className="mb-4 gap-5 bg-darkGray pt-8 w-full flex-nowrap">
                {EDIT_PERSONA_TABS.map((tab) => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="data-[state=active]:font-bold data-[state=active]:bg-darkGray data-[state=active]:border-b-[2px] align-middle"
                  >
                    {tab.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <div className="mt-6">
              <TabsContent
                value="profile"
                className={cn(tab !== "profile" && "hidden")}
              >
                <BasicInfoStep
                  form={basicInfoForm}
                  readOnly={formReadOnly}
                  onQuickCreateClick={handleQuickCreateClick}
                />
              </TabsContent>

              <TabsContent
                value="appearance"
                className={cn(tab !== "appearance" && "hidden")}
              >
                <AppearanceStep
                  form={appearanceForm}
                  readOnly={formReadOnly}
                  onQuickCreateClick={handleQuickCreateClick}
                />
              </TabsContent>

              <TabsContent
                value="connect"
                className={cn(tab !== "connect" && "hidden")}
              >
                <AccountConfigStep
                  form={accountConfigForm}
                  readOnly={formReadOnly}
                  personaData={personaData}
                />
              </TabsContent>

              <TabsContent
                value="personality"
                className={cn(tab !== "personality" && "hidden")}
              >
                <AdditionalBackgroundStep
                  form={additionalBackgroundForm}
                  readOnly={formReadOnly}
                  onQuickCreateClick={handleQuickCreateClick}
                />
              </TabsContent>

              <TabsContent
                value="knowledge"
                className={cn(tab !== "knowledge" && "hidden")}
              >
                <KnowledgeBasesStep
                  form={knowledgeBasesForm}
                  readOnly={formReadOnly}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 justify-between mt-8 mx-10">
          {children}
          <div className="flex flex-col flex-grow sm:flex-row gap-2 justify-end">
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <LoadingButton
              onClick={() => handleSave()}
              disabled={!isAnyFormDirty || isLoading} // Corrected: Use the combined dirty flag
              loading={isLoading}
            >
              Save
            </LoadingButton>
          </div>
        </div>
      </div>

      <StepChangeConfirmationDialog
        open={blocker.state === "blocked"}
        onClose={handleConfirmationDialogClose}
        loading={isLoading}
      />

      <QuickCreatePersonaModal
        isOpen={isQuickCreateOpen}
        onClose={handleQuickCreateClose}
        onSuccess={handleQuickCreateSuccess}
        personaId={personaData?.persona_id}
        personaName={personaData?.name}
        currentPersonaData={personaData}
      />
    </>
  );
}

function StepChangeConfirmationDialog({
  open,
  onClose,
  loading,
}: {
  open: boolean;
  onClose: (reason: "cancel" | "save" | "navigate") => void;
  loading: boolean;
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose("cancel")}
    >
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Do you want to save them before leaving?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={loading}
            onClick={() => onClose("cancel")}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              buttonVariants({ variant: "outline" }),
              "text-white mt-2 sm:mt-0",
            )}
            onClick={(e) => {
              e.preventDefault();
              onClose("navigate");
            }}
            disabled={loading}
          >
            Leave Without Saving
          </AlertDialogAction>
          <AlertDialogAction
            asChild
            onClick={(e) => {
              e.preventDefault();
              onClose("save");
            }}
          >
            <LoadingButton loading={loading}>Save and Leave</LoadingButton>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

async function validateForm<T extends FieldValues>(form: UseFormReturn<T>) {
  const isValid = await form.trigger();
  const values = form.getValues();
  const isDirty = form.formState.isDirty;
  return { isValid, values, isDirty };
}
